import { useState, useEffect, useCallback } from 'react';

import { toggleAppUpdateLike } from 'src/actions/app-updates/app-updates';

type UseLikeToggleOptions = {
  updateId: number;
  initialLiked: boolean;
  initialLikeCount: number;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

type UseLikeToggleReturn = {
  liked: boolean;
  likeCount: number;
  isLiking: boolean;
  toggle: () => Promise<void>;
};

export function useLikeToggle({
  updateId,
  initialLiked,
  initialLikeCount,
  onSuccess,
  onError,
}: UseLikeToggleOptions): UseLikeToggleReturn {
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiking, setIsLiking] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    if (!hasUserInteracted) {
      setLiked(initialLiked);
      setLikeCount(initialLikeCount);
    }
  }, [initialLiked, initialLikeCount, hasUserInteracted]);

  const toggle = useCallback(async (): Promise<void> => {
    if (isLiking) return;

    setHasUserInteracted(true);
    setIsLiking(true);
    const previousLiked = liked;
    const previousCount = likeCount;

    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);

    try {
      const result = await toggleAppUpdateLike(updateId);
      setLikeCount(result.likeCount);
      onSuccess?.();
    } catch (error) {
      setLiked(previousLiked);
      setLikeCount(previousCount);
      onError?.(error);
    } finally {
      setIsLiking(false);
    }
  }, [isLiking, liked, likeCount, updateId, onSuccess, onError]);

  return { liked, likeCount, isLiking, toggle };
}
